import {
  FolderListAction,
  FolderListReducerProps,
  KnowledgeBaseProps,
  UploadKBFolderState,
} from './kb.props';

export const initialStatesKnowledgeBaseReducer = {
  modalOpen: false,
  saving: 'Save',
  error: '',
  providers: [],
  filteredModels: [],
  dataUpdated: false,
  loading: true,
  logout: false,
  formDetails: {
    name: '',
    model: 0,
    service: '' as 'system' | 'G-drive' | 'aws',
  },
  showError: false,
  errorModel: '',
  errorService: '',
  service: '',
};

export const initialStateFolderListReducer: FolderListReducerProps = {
  deleteModalOpen: false,
  shareModalOpen: false,
  uploadModalOpen: false,
  dataToDelete: [],
  selectAll: false,
  totalTableData: 0,
  loading: true,
  logout: false,
  dataUpdated: false,
  credentials: [],
  editModalOpen: false,
  dataToEdit: { id: 0, name: '' },
  service: '' as 'system' | 'G-drive' | 'aws',
};

export const initialStatesUploadKnowledgeBaseFolderReducer: UploadKBFolderState = {
  files: [] as File[],
  showUploadArea: false,
  chunkBy: '',
  chunk_words: 0,
  chunk_overlap: 0,
  folderId: 0,
  showError: false,
  showOverLapError: '',
  service: '' as 'system' | 'G-drive' | 'aws',
  credentials: {} as unknown,
  folderStructureModalOpen: false,
  openAuthAwsModal: false,
  upload_disable: false,
  loading: false,
};

export const KnowledgeBaseReducer = (
  state: KnowledgeBaseProps,
  action: FolderListAction,
): KnowledgeBaseProps => {
  switch (action.type) {
    case 'SET_SERVICE':
      return {
        ...state,
        service: action.payload,
      };
    case 'TOGGLE_FOLDER_MODAL':
      return { ...state, modalOpen: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_ERROR_MODEL':
      return { ...state, errorModel: action.payload };
    case 'SET_ERROR_SERVICE':
      return { ...state, errorService: action.payload };
    case 'SET_PROVIDERS':
      return { ...state, providers: action.payload };
    case 'SET_FILTERED_MODELS':
      return { ...state, filteredModels: action.payload };
    case 'SET_FORM_DETAILS':
      return {
        ...state,
        formDetails: {
          ...state.formDetails,
          ...action.payload,
          service: (action.payload.service || state.formDetails.service) as
            | 'system'
            | 'G-drive'
            | 'aws',
        },
      };
    case 'DATA_UPDATED':
      return { ...state, dataUpdated: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_LOGOUT':
      return { ...state, logout: action.payload };
    case 'SHOW_ERROR':
      return { ...state, showError: action.payload };
    default:
      return state;
  }
};

export const FolderListReducer = (
  state: FolderListReducerProps,
  action: FolderListAction,
): FolderListReducerProps => {
  switch (action.type) {
    case 'SET_REDIRECT':
      return { ...state, redirect: action.payload };
    case 'SET_CREDENTIALS':
      return { ...state, credentials: action.payload };
    case 'SET_SERVICE':
      return { ...state, service: action.payload as 'system' | 'G-drive' | 'aws' };
    case 'TOGGLE_DELETE_MODAL':
      return { ...state, deleteModalOpen: action.payload };
    case 'TOGGLE_UPLOAD_MODAL':
      return { ...state, uploadModalOpen: action.payload };
    case 'TOGGLE_SHARE_MODAL':
      return { ...state, shareModalOpen: action.payload };
    case 'SET_DATA_TO_DELETE':
      return { ...state, dataToDelete: action.payload };
    case 'SET_SELECT_ALL':
      return { ...state, selectAll: action.payload };
    case 'SET_TOTAL_TABLE_DATA':
      return { ...state, totalTableData: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_LOGOUT':
      return { ...state, logout: action.payload };
    case 'DATA_UPDATED':
      return { ...state, dataUpdated: action.payload };
    case 'TOGGLE_EDIT_MODAL':
      return { ...state, editModalOpen: action.payload };
    case 'FILE_TO_EDIT':
      return { ...state, dataToEdit: action.payload };
    case 'TOGGLE_FOLDER_MODAL':
      return { ...state, modalOpen: action.payload };
    default:
      return state;
  }
};

export type UploadKnowledgeBaseAction =
  | { type: 'FOLDER_STRUCTURE_MODAL_OPEN'; payload: boolean }
  | { type: 'AWS_AUTH_MODAL'; payload: boolean }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'TOGGLE_UPLOAD_AREA'; payload: boolean }
  | { type: 'SET_CREDENTIALS'; payload: unknown }
  | { type: 'SET_SERVICE'; payload: 'system' | 'G-drive' | 'aws' }
  | { type: 'SET_UPLOAD_DISABLE'; payload: boolean }
  | { type: 'ADD_FILES'; payload: File[] }
  | { type: 'REMOVE_FILE'; payload: { key: string; value: string } | number }
  | { type: 'SET_VALUE'; payload: { key: string; value: string | number } }
  | { type: 'RESET_UPLOAD'; payload?: boolean | ((arg: boolean) => void) }
  | { type: 'SHOW_ERROR'; payload: boolean | string }
  | { type: 'SHOW_OVERLAP_ERROR'; payload: string }
  | { type: 'FOLDER_ID'; payload: number };

export const UploadKnowledgeBaseFolderReducer = (
  state: UploadKBFolderState,
  action: UploadKnowledgeBaseAction,
): UploadKBFolderState => {
  switch (action.type) {
    case 'FOLDER_ID':
      return { ...state, folderId: action.payload };
    case 'FOLDER_STRUCTURE_MODAL_OPEN':
      return { ...state, folderStructureModalOpen: action.payload };
    case 'AWS_AUTH_MODAL':
      return { ...state, openAuthAwsModal: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_UPLOAD_DISABLE':
      return { ...state, loading: action.payload };
    case 'SET_CREDENTIALS':
      return { ...state, credentials: action.payload };
    case 'SET_SERVICE':
      return {
        ...state,
        service: action.payload,
      };
    case 'TOGGLE_UPLOAD_AREA':
      return { ...state, showUploadArea: !state.showUploadArea };
    case 'ADD_FILES':
      return {
        ...state,
        files: [...state.files, ...action.payload],
        showUploadArea: false,
      };
    case 'REMOVE_FILE':
      return {
        ...state,
        files: state.files.filter((_, i) => i !== action.payload),
      };
    case 'SET_VALUE':
      return { ...state, [action.payload.key]: action.payload.value };
    case 'RESET_UPLOAD':
      return {
        ...initialStatesUploadKnowledgeBaseFolderReducer,
      };
    case 'SHOW_ERROR':
      return { ...state, showError: action.payload };
    case 'SHOW_OVERLAP_ERROR':
      return { ...state, showOverLapError: action.payload };
    default:
      return state;
  }
};
